// components/plScenario/table/CategorySection.jsx
import React from 'react';
import { ChevronDownIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import LineItemRow from './LineItemRow';
import TemporaryRow from './TemporaryRow';

const CategorySection = ({
  title,
  items,
  sectionKey,
  bgColor = 'bg-gray-100',
  indent = 0,
  mainSection = '',
  viewMode,
  months,
  quarters,
  isCollapsed,
  onToggleSection,
  calculateGroupTotal,
  calculateQuarterlyTotal,
  temporaryRows,
  onAddTemporaryRow,
  formatCurrency,
  // Pass through props for child components
  ...childProps
}) => {
  const groupKey = `${mainSection}-${sectionKey}`;
  const tempRowsForGroup = temporaryRows[groupKey] || [];
  
  if (items.length === 0 && tempRowsForGroup.length === 0) {
    return null;
  }

  const indentClass = indent === 0 ? 'pl-4' : 'pl-8';
  const collapsed = isCollapsed(sectionKey);

  return (
    <>
      <tr 
        className={`${bgColor} cursor-pointer hover:bg-opacity-80`}
        onClick={() => onToggleSection(sectionKey)}
      >
        <td className={`${indentClass} py-3 text-xs text-gray-900 border-b border-gray-200`}>
          <div className="flex items-center">
            {collapsed ? (
              <ChevronRightIcon className="w-3 h-3 mr-2" />
            ) : (
              <ChevronDownIcon className="w-3 h-3 mr-2" />
            )}
            {title}
          </div>
        </td>
        
        {viewMode === 'monthly' ? (
          months.map((month) => {
            const total = calculateGroupTotal(items, month.month_num, groupKey);
            return (
              <td
                key={month.month_num}
                className={`px-2 py-1.5 text-center text-xs border-b border-gray-200 ${
                  month.is_actual ? 'bg-white' : 'bg-blue-50'
                }`}
              >
                {formatCurrency(total)}
              </td>
            );
          })
        ) : (
          quarters.map((quarter) => {
            const total = calculateQuarterlyTotal(items, quarter.months, groupKey);
            return (
              <td
                key={quarter.quarter}
                className={`px-2 py-1.5 text-center text-xs border-b border-gray-200 ${
                  quarter.is_actual ? 'bg-white' : 'bg-blue-50'
                }`}
              >
                {formatCurrency(total)}
              </td>
            );
          })
        )}
        
        <td className="px-4 py-1.5 text-center text-xs bg-yellow border-b border-gray-200">
          {formatCurrency(
            items.reduce(
              (sum, item) =>
                sum + Object.values(item.projected_amounts).reduce((s, v) => s + v, 0),
              0
            ) + tempRowsForGroup.reduce(
              (sum, tempRow) =>
                sum + Object.values(tempRow.values).reduce((s, v) => s + v, 0),
              0
            )
          )}
        </td>
      </tr>

      {!collapsed && (
        <>
          {items.map(item => (
            <LineItemRow
                key={item.account_id}
                item={item}
                indent={indent + 1}
                viewMode={viewMode}
                months={months}
                quarters={quarters}
                formatCurrency={formatCurrency}
                setRowRef={childProps.setRowRef} // Make sure this is passed
                {...childProps}
            />
            ))}

          {tempRowsForGroup.map(tempRow => (
            <TemporaryRow
              key={tempRow.id}
              tempRow={tempRow}
              viewMode={viewMode}
              months={months}
              quarters={quarters}
              formatCurrency={formatCurrency}
              {...childProps}
            />
          ))}
          <AddRowButton groupKey={groupKey} label={title} onAdd={onAddTemporaryRow} />
        </>
      )}
    </>
  );
};

const AddRowButton = ({ groupKey, label, onAdd }) => {
  return (
    <tr className="hover:bg-gray-50">
      <td colSpan={14} className="py-2 px-12">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAdd(groupKey);
          }}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg border border-blue-300 border-dashed transition-colors"
        >
          <PlusIcon className="w-3 h-3" />
          Add Test Row to {label}
        </button>
      </td>
    </tr>
  );
};

export default CategorySection;